// T90: specyfikacja wypłaty („za co") i T91: karta „Eventy pracownika".
// Kto widzi (decyzja 08.10): admin, „Rozlicza wypłaty", opiekun eventowych — u wszystkich; pracownik — swoje.
// Pilnuje tego backend (GET /settlements/:id, GET /users/:id/events); tu tylko wyświetlanie.
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, fullName, workLabel, bonusLabel, useDateFilter, DateFilter, fmtDay } from './UI';
import api from '../utils/api';

const zl = (v) => `${(Number(v) || 0).toFixed(2).replace('.', ',')} zł`;
const fmt = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : '—';

/** Tabela „za co": wpisy rozliczenia (z podglądu albo z GET /settlements/:id). */
export function SpecTable({ entries }) {
  if (!entries?.length) return <div style={{ fontSize: 11, color: '#94A3B8', padding: '4px 0' }}>Brak wpisów.</div>;
  const total = entries.reduce((a, e) => a + (Number(e.amount) || 0), 0);
  return (
    <table className="data-table" style={{ fontSize: 11, background: '#fff' }}>
      <thead><tr><th>Data</th><th>Za co</th><th>Czas</th><th>Godziny</th><th style={{ textAlign: 'right' }}>Kwota</th></tr></thead>
      <tbody>
        {entries.map(e => (
          <tr key={e.kind + e.id}>
            <td style={{ whiteSpace: 'nowrap' }}>{fmt(e.date)}</td>
            <td>
              {e.kind === 'hours'
                ? <>{e.event ? <>🎪 <b>{e.event.name}</b>{e.event.location ? <span style={{ color: '#64748B' }}> · {e.event.location}</span> : null}</> : <b>{workLabel(e.workType)}</b>}
                    {e.event && e.workType !== 'EVENT' && <span style={{ color: '#64748B' }}> ({workLabel(e.workType)})</span>}</>
                : <>🧾 {bonusLabel(e.bonusType)}{e.event ? <span style={{ color: '#64748B' }}> · {e.event.name}</span> : null}
                    {e.description && <div style={{ color: '#64748B', fontStyle: 'italic' }}>{e.description}</div>}</>}
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>{e.kind === 'hours' && e.startTime ? `${e.startTime}–${e.endTime || '?'}` : ''}</td>
            <td>{e.kind === 'hours' ? (e.daily ? 'dniówka' : `${e.hours} h`) : ''}
              {e.kind === 'hours' && e.inNorm && <div style={{ fontSize: 10, color: e.overtime > 0 ? '#059669' : '#94A3B8' }}>{e.workType === 'NIGHT_WAREHOUSE' ? 'nocne — stawka nocna' : e.overtime > 0 ? `w tym ${e.overtime} h nadgodzin` : 'w normie (pensja)'}</div>}</td>
            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{zl(e.amount)}</td>
          </tr>
        ))}
        <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Razem</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{zl(total)}</td></tr>
      </tbody>
    </table>
  );
}

/** Rozwijana specyfikacja zapisanego rozliczenia — dociąga wpisy przy pierwszym rozwinięciu. */
export function SettlementSpec({ id, initiallyOpen = false, label = 'za co', bare = false }) {
  const [open, setOpen] = useState(initiallyOpen || bare);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open || data) return;
    api.getSettlement(id).then(setData).catch(e => setErr(e.message));
  }, [open, id, data]);
  return (
    <div>
      {!bare && <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', fontSize: 11 }} onClick={() => setOpen(o => !o)}>{open ? '▾' : '▸'} {label}</button>}
      {open && (err ? <div style={{ fontSize: 11, color: '#DC2626' }}>{err}</div>
        : data ? <div style={{ marginTop: 4 }}><SpecTable entries={data.entries} /></div>
        : <div style={{ fontSize: 11, color: '#94A3B8' }}>Ładowanie…</div>)}
    </div>
  );
}

/** Pobiera specyfikację kilku rozliczeń (np. całego dnia) — do pliku CSV. */
export async function loadSpecs(ids) {
  const out = [];
  for (const id of ids) out.push(await api.getSettlement(id));
  return out;
}

// ---------- T91: karta „Eventy pracownika" ----------
const participation = (ev) => {
  const a = ev.assignment;
  const done = ev.hours.length > 0;
  const past = new Date(ev.date) < new Date(new Date().toDateString());
  if (done) return <span style={{ color: '#059669' }}>✓ był</span>;
  if (ev.status === 'CANCELLED') return <span style={{ color: '#94A3B8' }}>event anulowany</span>;
  if (a?.declinedAt) return <span style={{ color: '#DC2626' }}>✗ odmówił{a.declineReason ? `: ${a.declineReason}` : ''}</span>;
  if (past) return <span style={{ color: '#B45309', fontWeight: 600 }}>⚠ w składzie, brak wpisu godzin</span>;
  if (a?.confirmedAt) return <span style={{ color: '#059669' }}>✓ potwierdził</span>;
  return <span style={{ color: '#94A3B8' }}>— brak odpowiedzi</span>;
};
const entryStatus = (x) => {
  if (x.settlement) return <span style={{ color: '#7C3AED' }}>💰 wypłacone {fmt(x.settlement.createdAt)}</span>;
  if (x.status === 'APPROVED') return <span style={{ color: '#059669' }}>✓ zatwierdzone{x.approvedBy ? ` (${x.approvedBy.firstName} ${(x.approvedBy.lastName || '').slice(0, 1)}.)` : ''}</span>;
  if (x.status === 'REJECTED') return <span style={{ color: '#DC2626' }}>✗ odrzucone</span>;
  return <span style={{ color: '#94A3B8' }}>⏳ czeka na zatwierdzenie</span>;
};

/** Okno: na jakich eventach była osoba (z godzinami, wydatkami, statusem wypłaty) + podsumowanie. */
export function PersonEventsModal({ userId, name, onClose }) {
  const df = useDateFilter('year');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    setErr('');
    api.getPersonEvents(userId, `from=${df.from}&to=${df.to}`).then(setData).catch(e => setErr(e.message));
  }, [userId, df.from, df.to]);
  useEffect(() => { load(); }, [load]);

  const events = data?.events || [];
  const sum = { events: 0, hours: 0, daily: 0, paid: 0, approved: 0, pending: 0, missing: 0 };
  for (const ev of events) {
    if (ev.hours.length) sum.events += 1;
    if (!ev.hours.length && !ev.assignment?.declinedAt && ev.status !== 'CANCELLED' && new Date(ev.date) < new Date(new Date().toDateString())) sum.missing += 1;
    for (const x of [...ev.hours.map(h => ({ ...h, amt: Number(h.totalAmount) })), ...ev.bonuses.map(b => ({ ...b, amt: Number(b.amount) }))]) {
      if (x.workType) { if (x.workType === 'EVENT_DAILY') sum.daily += 1; else if (x.status !== 'REJECTED') sum.hours += Number(x.totalHours) || 0; }
      if (x.settlement) sum.paid += x.amt; else if (x.status === 'APPROVED') sum.approved += x.amt; else if (x.status === 'PENDING') sum.pending += x.amt;
    }
  }
  const title = `🎪 Eventy: ${data?.person ? fullName(data.person) : (name || '')}`;
  return (
    <Modal title={title} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo} onFilter={load} quickFilter={df.quick} onQuickFilter={df.handleQuick} />
        {err && <div style={{ color: '#DC2626', fontSize: 12 }}>{err}</div>}
        {data && (
          <div className="flex gap-8 flex-wrap" style={{ fontSize: 12 }}>
            <span>Eventy z wpisem: <b>{sum.events}</b></span>
            <span>Godziny: <b>{Math.round(sum.hours * 100) / 100} h</b>{sum.daily ? <> · dniówki: <b>{sum.daily}</b></> : null}</span>
            <span style={{ color: '#7C3AED' }}>Wypłacone: <b>{zl(sum.paid)}</b></span>
            <span style={{ color: '#059669' }}>Do wypłaty: <b>{zl(sum.approved)}</b></span>
            {sum.pending > 0 && <span style={{ color: '#94A3B8' }}>Czeka: {zl(sum.pending)}</span>}
            {sum.missing > 0 && <span style={{ color: '#B45309', fontWeight: 600 }}>⚠ Bez wpisu godzin: {sum.missing}</span>}
          </div>
        )}
        <div className="table-wrap" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <table className="data-table" style={{ fontSize: 11 }}>
            <thead><tr><th>Data</th><th>Event</th><th>Udział</th><th>Godziny / wydatki</th><th>Kwota</th><th>Status</th></tr></thead>
            <tbody>
              {!data && !err && <tr><td colSpan={6} style={{ color: '#94A3B8', padding: 16 }}>Ładowanie…</td></tr>}
              {data && events.length === 0 && <tr><td colSpan={6} style={{ color: '#94A3B8', padding: 16, textAlign: 'center' }}>W tym okresie brak eventów</td></tr>}
              {events.map(ev => {
                const items = [...ev.hours.map(h => ({ ...h, kind: 'h' })), ...ev.bonuses.map(b => ({ ...b, kind: 'b' }))];
                return (
                  <tr key={ev.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(ev.date)}</td>
                    <td><b>{ev.name}</b>{ev.location && <div style={{ color: '#64748B' }}>{ev.location}</div>}{ev.status === 'CANCELLED' && <div style={{ color: '#94A3B8' }}>anulowany</div>}</td>
                    <td>{participation(ev)}</td>
                    <td>{items.length ? items.map(x => (
                      <div key={x.kind + x.id}>{x.kind === 'h'
                        ? <>{x.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(x.totalHours)} h`}{x.startTime ? ` (${x.startTime}–${x.endTime})` : ''}{x.workType !== 'EVENT' && x.workType !== 'EVENT_DAILY' ? ` · ${workLabel(x.workType)}` : ''} <span style={{ color: '#94A3B8' }}>dodano {fmtDay(x.createdAt)}</span></>
                        : <>🧾 {bonusLabel(x.bonusType)}{x.description ? ` — ${x.description}` : ''}</>}</div>
                    )) : <span style={{ color: '#CBD5E1' }}>—</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{items.map(x => <div key={x.kind + x.id}>{zl(x.kind === 'h' ? x.totalAmount : x.amount)}</div>)}</td>
                    <td>{items.map(x => <div key={x.kind + x.id}>{entryStatus(x)}</div>)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
